// InstallationCommon.js - Shared imports and utilities for Installation Configuration
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { authFetch, authFetchRequest } from '../utils/api';
import { alertC, confirmC } from '../components/dialogService';
import { useProductScope } from '../utils/productScope';

// Re-export all components for use by tab components
export {
  React,
  useEffect,
  useState,
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC
};

// Shared InstallationDialog component used by all installation tabs
export const InstallationDialog = ({ open, data, title = "Item", onClose, onSave }) => {
  const { defaultScope, optionsForValue } = useProductScope();
  const [formData, setFormData] = useState({ label: '', value: '', applies_to: 'windows', active: true, sort_order: 0 });

  useEffect(() => {
    if (data) {
      setFormData({
        label: data.label || '',
        value: data.value || '',
        applies_to: data.applies_to || defaultScope,
        active: data.active ?? true,
        sort_order: data.sort_order || 0
      });
    } else {
      setFormData({ label: '', value: '', applies_to: defaultScope, active: true, sort_order: 0 });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? `Edit ${title}` : `Add ${title}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Label" value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} fullWidth />
          <TextField label="Value" value={formData.value} onChange={(e) => setFormData({ ...formData, value: e.target.value })} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Applies To</InputLabel>
            <Select
              value={formData.applies_to}
              label="Applies To"
              onChange={(e) => setFormData({ ...formData, applies_to: e.target.value })}
            >
              {optionsForValue(formData.applies_to).map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Sort Order" type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} fullWidth />
          <FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />} label="Active" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

// Shared InstallationColorDialog component used by color tabs (e.g. Brickmold Color)
export const InstallationColorDialog = ({ open, data, title = "Color", onClose, onSave }) => {
  const { defaultScope, optionsForValue } = useProductScope();
  const [formData, setFormData] = useState({ label: '', value: '#FFFFFF', applies_to: 'windows', active: true, sort_order: 0 });

  useEffect(() => {
    if (data) {
      setFormData({
        label: data.label || '',
        value: data.value || '#FFFFFF',
        applies_to: data.applies_to || defaultScope,
        active: data.active ?? true,
        sort_order: data.sort_order || 0
      });
    } else {
      setFormData({ label: '', value: '#FFFFFF', applies_to: defaultScope, active: true, sort_order: 0 });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? `Edit ${title}` : `Add ${title}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Label" value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} fullWidth />
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Color Code (Hex)"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              fullWidth
              placeholder="#FFFFFF"
            />
            <input
              type="color"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              style={{ width: 50, height: 40, padding: 0, border: 'none', cursor: 'pointer' }}
            />
          </Stack>
          <FormControl fullWidth>
            <InputLabel>Applies To</InputLabel>
            <Select
              value={formData.applies_to}
              label="Applies To"
              onChange={(e) => setFormData({ ...formData, applies_to: e.target.value })}
            >
              {optionsForValue(formData.applies_to).map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Sort Order" type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} fullWidth />
          <FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />} label="Active" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

// Common info box component
export const InfoBox = ({ children }) => (
  <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
    <span style={{ fontSize: '0.85em', color: '#555' }}>
      {children || "Note: Feel free to change the label (name shown in GUI, quotes, orders, invoices, packages). Do not change the value unless you fully understand what you're doing."}
    </span>
  </Box>
);
